import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import {
    Activity,
    ArrowRight,
    CheckCircle2,
    Star,
} from "lucide-react";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "../components/ui/accordion";
import Seo from "../lib/Seo";
import CenterCard from "../components/CenterCard";
import Breadcrumbs from "../components/Breadcrumbs";
import { fetchCenters } from "../lib/api";
import { getModality, SEO_MODALITIES, MODALITY_SLUGS } from "../lib/seoPages";

// Map slug → API filter parameters
function buildApiParams(modality) {
    const map = {
        residencial: { modality: "Residencial" },
        ambulatoria: { modality: "Ambulatorio" },
        online: { modality: "Online" },
        mujeres: { gender: "mujer" },
        adolescentes: {}, // No specific filter; use editorial content
    };
    return { ...(map[modality.slug] || {}), page: 1, limit: 12 };
}

export default function ModalityDetail() {
    const { slug } = useParams();
    const modality = getModality(slug);

    const [centers, setCenters] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!modality) return;
        const controller = new AbortController();
        let cancelled = false;
        setLoading(true);
        fetchCenters(buildApiParams(modality), { signal: controller.signal })
            .then((data) => {
                if (cancelled) return;
                setCenters(data.centers || []);
                setTotal(data.total || 0);
            })
            .catch(() => {
                if (cancelled) return;
                setCenters([]);
                setTotal(0);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
            controller.abort();
        };
    }, [modality]);

    if (!modality) return <Navigate to="/centros" replace />;

    const faqJsonLd = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: (modality.faq || []).map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
    };

    const related = MODALITY_SLUGS.filter((s) => s !== slug)
        .slice(0, 4)
        .map((s) => SEO_MODALITIES[s]);

    return (
        <>
            <Seo
                title={modality.metaTitle || modality.h1}
                description={modality.metaDescription}
                path={`/modalidades/${modality.slug}`}
                keywords={`${modality.label}, tratamiento ${modality.label}, centros ${modality.name.toLowerCase()} chile`}
                jsonLd={faqJsonLd}
            />

            <section className="border-b border-slate-800 bg-slate-950">
                <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
                    <Breadcrumbs
                        items={[
                            { name: "Inicio", path: "/" },
                            { name: "Modalidades", path: "/modalidades" },
                            { name: modality.name },
                        ]}
                    />
                    <div className="mt-5 inline-flex items-center gap-2 rounded-full border border-cyan-500/30 bg-cyan-500/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-cyan-300">
                        <Activity className="h-3.5 w-3.5" />
                        Modalidad · {modality.name}
                    </div>
                    <h1
                        data-testid="modality-h1"
                        className="mt-4 text-3xl font-extrabold leading-tight tracking-tight text-slate-50 sm:text-5xl"
                    >
                        {modality.h1}
                    </h1>
                    <p className="mt-5 max-w-3xl text-base leading-relaxed text-slate-400 sm:text-lg">
                        {modality.intro}
                    </p>
                </div>
            </section>

            <section className="border-b border-slate-800 py-14">
                <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
                    <h2 className="text-2xl font-bold tracking-tight text-slate-50 sm:text-3xl">
                        Cómo es la oferta de {modality.name.toLowerCase()} en Chile
                    </h2>
                    <p className="mt-4 leading-relaxed text-slate-300">
                        {modality.contexto}
                    </p>
                </div>
            </section>

            <section className="border-b border-slate-800 py-14">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-wrap items-end justify-between gap-4">
                        <div>
                            <div className="inline-flex items-center gap-2 rounded-full border border-cyan-500/30 bg-cyan-500/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-cyan-300">
                                <Star className="h-3.5 w-3.5 fill-cyan-400 text-cyan-400" />
                                Centros con esta modalidad
                            </div>
                            <h2 className="mt-4 text-2xl font-bold tracking-tight text-slate-50 sm:text-3xl">
                                Centros disponibles
                            </h2>
                            <p className="mt-2 text-slate-400">
                                {loading
                                    ? "Cargando centros..."
                                    : `${total} centro${total === 1 ? "" : "s"} con ${modality.label}.`}
                            </p>
                        </div>
                        <Link
                            to="/centros"
                            className="group inline-flex items-center gap-1 rounded-md border border-slate-800 bg-slate-900 px-4 py-2 text-sm font-medium text-cyan-400 hover:border-cyan-500/40 hover:text-cyan-300"
                        >
                            Ver con filtros
                            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                        </Link>
                    </div>
                    <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                        {loading
                            ? Array.from({ length: 6 }).map((_, i) => (
                                  <div
                                      key={i}
                                      className="h-80 animate-pulse rounded-xl border border-slate-800 bg-slate-900"
                                  />
                              ))
                            : centers.map((c) => <CenterCard key={c.center_id} center={c} />)}
                    </div>
                </div>
            </section>

            <section className="border-b border-slate-800 py-14">
                <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
                    <h2 className="text-2xl font-bold tracking-tight text-slate-50 sm:text-3xl">
                        Cómo elegir un programa de {modality.label}
                    </h2>
                    <p className="mt-4 leading-relaxed text-slate-300">
                        {modality.comoElegir}
                    </p>
                    <ul className="mt-6 space-y-3">
                        {[
                            "Equipo clínico titulado y con experiencia específica en adicciones.",
                            "Plan de tratamiento personalizado, no rutina genérica para todos.",
                            "Seguimiento post-alta de al menos 6 meses incluido en el programa.",
                            "Comunicación honesta sobre lo que sí y no logra esta modalidad.",
                            "Presupuesto escrito y políticas claras sobre interrupciones / reembolsos.",
                        ].map((txt, i) => (
                            <li
                                key={i}
                                className="flex gap-3 rounded-xl border border-slate-800 bg-slate-900 p-4"
                            >
                                <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-cyan-400" />
                                <span className="text-slate-300">{txt}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            <section className="border-b border-slate-800 py-14">
                <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
                    <div className="inline-flex items-center gap-2 rounded-full border border-cyan-500/30 bg-cyan-500/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-cyan-300">
                        Preguntas frecuentes
                    </div>
                    <h2 className="mt-4 text-2xl font-bold tracking-tight text-slate-50 sm:text-3xl">
                        Sobre {modality.label}
                    </h2>
                    <Accordion
                        type="single"
                        collapsible
                        className="mt-6 rounded-xl border border-slate-800 bg-slate-900"
                        data-testid="modality-faq"
                    >
                        {(modality.faq || []).map((f, i) => (
                            <AccordionItem
                                key={i}
                                value={`m-faq-${i}`}
                                className="border-slate-800 px-5"
                            >
                                <AccordionTrigger className="text-left text-slate-100 hover:text-cyan-400">
                                    {f.q}
                                </AccordionTrigger>
                                <AccordionContent className="text-slate-400">
                                    {f.a}
                                </AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </div>
            </section>

            <section className="py-14">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <h2 className="text-2xl font-bold tracking-tight text-slate-50 sm:text-3xl">
                        Otras modalidades
                    </h2>
                    <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                        {related.map((r) => (
                            <Link
                                key={r.slug}
                                to={`/modalidades/${r.slug}`}
                                className="card-hover rounded-xl border border-slate-800 bg-slate-900 p-5"
                            >
                                <div className="flex items-center gap-2 text-cyan-400">
                                    <Activity className="h-4 w-4" />
                                    <span className="text-xs font-medium uppercase tracking-wider">
                                        Modalidad
                                    </span>
                                </div>
                                <h3 className="mt-3 text-lg font-semibold text-slate-50">
                                    {r.name}
                                </h3>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>
        </>
    );
}
